/**
 * External Imports
 */
import { FC, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { format, parse, sub, startOfQuarter } from 'date-fns';

/**
 * Nexus Imports
 */

import { NeuInput } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */

import { Dropdown } from '../../../shared/dropdowns';
import MonthSelectsSection from '../../../searchHeader/customDateRangeSelect/monthSelectsSection';
import { logReportsClick } from '../../../../utils/analyticsHelpers';
import { date as dt, unCamelCase } from '../../../../utils/helpers';
import TemplateMultiSelectSection from './TemplateMultiSelectSection';
/**
 * Global Type Definition Imports
 */
import { Filter } from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  InputHeading,
  SelectContainer,
  StartEnd
} from './EmployeeReportsFilterSection.styles';

interface IEmployeeReportsFilterSectionProps {
  filterValues: Filter;
  setFilterValues: (data: Filter) => void;
}

const dateRanges = [
  { value: 'custom', displayName: 'Custom' },
  { value: 'monthToDate', displayName: 'Month to date' },
  { value: 'lastFullMonth', displayName: 'Last full month' },
  { value: 'lastFullQuarter', displayName: 'Last full quarter' }
];

const EmployeeReportsFilterSection: FC<IEmployeeReportsFilterSectionProps> = ({
  filterValues,
  setFilterValues
}) => {
  const [reportDateRangeType, setReportDateRangeType] =
    useState<string>('Month to date');
  const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
  const [open, setOpen] = useState(false);

  const { logTrackingEvent } = useAnalyticsApi();
  const view = useLocation().pathname.split('reports/')[1];

  // **
  // * Date Logic
  // **

  useEffect(() => {
    if (filterValues.dateRangeText === 'Custom') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
    }
    setReportDateRangeType(filterValues.dateRangeText || 'Month to date');
  }, [filterValues]);

  const handleMonthSelects = (e: any) => {
    const { value } = e.detail;
    const parsedMonth = value.split('-');
    const year = Number(parsedMonth[0]);
    const month = Number(parsedMonth[1]) - 1;
    setFilterValues({
      ...filterValues,
      startDate: format(new Date(year, month, 1), 'yyyy-MM-dd'),
      endDate: format(
        sub(new Date(year, month + 1), { days: 1 }),
        'yyyy-MM-dd'
      ),
      dateRangeText: 'Custom'
    });
  };

  const handleStartSelect = (e: any) => {
    const { value } = e.detail;
    setFilterValues({
      ...filterValues,
      startDate: value || filterValues.startDate
    });
  };

  const handleEndSelect = (e: any) => {
    const { value } = e.detail;
    setFilterValues({
      ...filterValues,
      endDate: value || filterValues.endDate
    });
  };

  const handleDateRangeSelect = (range: {
    value: string;
    displayName: string;
  }) => {
    const selectedRange = range.value;
    setReportDateRangeType(unCamelCase(selectedRange));

    const today = new Date();

    if (selectedRange === 'monthToDate') {
      setShowCustomDate(false);

      const firstDayOfMonth = format(
        new Date(today.getFullYear(), today.getMonth(), 1),
        'yyyy-MM-dd'
      );

      const dayYesterday = format(today, 'yyyy-MM-dd');

      setFilterValues({
        ...filterValues,
        startDate: firstDayOfMonth,
        endDate: dayYesterday,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'lastFullMonth') {
      setShowCustomDate(false);

      const lastMonthFirstDay = format(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        'yyyy-MM-dd'
      );

      const lastMonthLastDay = format(
        sub(new Date(today.getFullYear(), today.getMonth(), 1), { days: 1 }),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: lastMonthFirstDay,
        endDate: lastMonthLastDay,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'lastFullQuarter') {
      setShowCustomDate(false);

      const lastQuarterLastDay = sub(startOfQuarter(today), { days: 1 });
      const lastQuarterLastDayFormatted = format(
        lastQuarterLastDay,
        'yyyy-MM-dd'
      );
      const lastQuarterFirstDay = format(
        startOfQuarter(
          new Date(
            lastQuarterLastDay.getFullYear(),
            lastQuarterLastDay.getMonth(),
            lastQuarterLastDay.getDate() - 1
          )
        ),
        'yyyy-MM-dd'
      );

      setFilterValues({
        ...filterValues,
        startDate: lastQuarterFirstDay,
        endDate: lastQuarterLastDayFormatted,
        dateRangeText: range.displayName
      });
    }

    if (selectedRange === 'custom') {
      setFilterValues({
        ...filterValues,
        dateRangeText: range.displayName
      });
      setShowCustomDate(true);
    }
  };

  const customMonth = useMemo(() => {
    let newDate = new Date();
    if (filterValues.startDate) {
      const parsedMonth = filterValues.startDate.split('-');
      const year = Number(parsedMonth[0]);
      const month = Number(parsedMonth[1]) - 1;
      newDate = new Date(year, month, 1);
    }
    return format(
      parse(format(newDate, 'yyyy-MM-dd'), 'yyyy-MM-dd', new Date()),
      'yyyy-MM'
    );
  }, [filterValues.startDate]);

  const minStartDate = useMemo(
    () => dt.convertToDayOfYear(sub(new Date(), { years: 2 })),
    []
  );
  const maxStartDate = useMemo(
    () =>
      filterValues.endDate &&
      dt.convertToDayOfYear(dt.localDayOfYearDate(filterValues.endDate)),
    [filterValues.endDate]
  );
  const minEndDate = useMemo(
    () =>
      filterValues.startDate &&
      dt.convertToDayOfYear(dt.localDayOfYearDate(filterValues.startDate)),
    [filterValues.startDate]
  );
  const maxEndDate = useMemo(() => dt.convertToDayOfYear(new Date()), []);

  useEffect(() => {}, [filterValues]);

  useEffect(() => {
    if (open) {
      switch (view) {
        case 'employee':
        case 'employee/compliance':
          logTrackingEvent(
            logReportsClick('employee-compliance', 'date-range')
          );
          break;
        case 'employee/rounds':
          logTrackingEvent(logReportsClick('employee-rounds', 'date-range'));
          break;
        case 'employee/frequency':
          logTrackingEvent(logReportsClick('employee-frequency', 'date-range'));
          break;
        default:
          console.log('No view for event');
      }
    }
  }, [open]);

  return (
    <>
      <div>
        <InputHeading>Date Range</InputHeading>
        <Dropdown
          classes="ps-0 pt-0"
          open={open}
          placeholder={reportDateRangeType}
          setOpen={setOpen}
        >
          {dateRanges.map(range => (
            <div
              key={range.value}
              aria-selected={unCamelCase(range.value) === reportDateRangeType}
              className={`dropdown-hover-item${
                unCamelCase(range.value) === reportDateRangeType
                  ? ' selected'
                  : ''
              }`}
              role="option"
              tabIndex={0}
              onClick={() => handleDateRangeSelect(range)}
              onKeyDown={() => handleDateRangeSelect(range)}
            >
              {range.displayName}
            </div>
          ))}
        </Dropdown>
        {showCustomDate && (
          <StartEnd className=" pt-2">
            {view === 'employee/frequency' ? (
              <>
                {/* TODO: This needs to be a datepicker */}
                <SelectContainer className="me-1">
                  <NeuInput
                    id="Reports-Freq-Start-Date"
                    label="Starting"
                    name="startDate"
                    type="date"
                    onNeuChange={handleStartSelect}
                    required
                    value={filterValues.startDate}
                    min={minStartDate}
                    max={maxStartDate}
                  />
                </SelectContainer>
                <SelectContainer className="ms-1">
                  <NeuInput
                    id="Reports-Freq-End-Date"
                    label="Ending"
                    name="endDate"
                    type="date"
                    onNeuChange={handleEndSelect}
                    required
                    value={filterValues.endDate}
                    min={minEndDate}
                    max={maxEndDate}
                  />
                </SelectContainer>
              </>
            ) : (
              <MonthSelectsSection
                handleSelects={handleMonthSelects}
                month={customMonth}
              />
            )}
          </StartEnd>
        )}
      </div>
      <div>
        {view === 'employee/frequency' && (
          <TemplateMultiSelectSection
            filterValues={filterValues}
            setFilterValues={setFilterValues}
          />
        )}
      </div>
    </>
  );
};

export default EmployeeReportsFilterSection;
